import Board from './Board';
import {
  NODE_COLOR_CLASS,
  TMT_GAME_NAME,
  type TmtStageConfig,
} from '@/pages/tmt/tmtConfig';

interface GameScreenProps {
  config: TmtStageConfig;
  path: string[];
  wrongId: string | null;
  completed: boolean;
  elapsedMs: number;
  transitionMsg: string | null;
  onSelect: (id: string) => void;
  onClose: () => void;
}

function formatTime(ms: number): string {
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export default function GameScreen({
  config,
  path,
  wrongId,
  completed,
  elapsedMs,
  transitionMsg,
  onSelect,
  onClose,
}: GameScreenProps) {
  const hasCondition = !!config.ruleColor;

  return (
    <div className="flex flex-col h-full px-4 pt-3 pb-4 bg-white animate-fade-in-up">
      {/* 헤더: 게임명 + 단계 + 경과시간 + 닫기 */}
      <div className="shrink-0 flex items-center justify-between mb-1">
        <div className="flex flex-col">
          <span className="text-base font-bold text-foreground-900 whitespace-nowrap leading-none">
            {TMT_GAME_NAME}
          </span>
          <span className="text-xs font-semibold text-primary-600 mt-1 whitespace-nowrap">
            {config.stageLabel}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="tabular-nums text-2xl font-semibold text-foreground-800 leading-none whitespace-nowrap">
            {formatTime(elapsedMs)}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full active:bg-secondary-100 transition-colors duration-150"
            aria-label="닫기"
          >
            <i className="ri-close-line text-xl text-foreground-700" />
          </button>
        </div>
      </div>

      {/* 현재 Stage 규칙 상시 표시 */}
      <div className="shrink-0 flex items-center justify-center mb-1">
        {hasCondition && (
          <span
            className={`inline-block w-4 h-4 shrink-0 ${
              NODE_COLOR_CLASS[config.ruleColor!]
            } ${config.ruleShape === 'square' ? 'rounded-[3px]' : 'rounded-full'} mr-1.5`}
          />
        )}
        <span className="text-sm font-semibold text-foreground-600 whitespace-nowrap">
          {config.ruleHint}
        </span>
      </div>

      {/* 노드 보드 */}
      <div className="flex-1 min-h-0 relative">
        <Board
          nodes={config.nodes}
          boardH={config.boardH}
          path={path}
          wrongId={wrongId}
          completed={completed}
          onSelect={onSelect}
          nodeSize={config.nodeSize}
        />

        {/* 완성 연출 */}
        {completed && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-5xl font-extrabold text-primary-600 animate-pop-in whitespace-nowrap">
              {config.completeMsg}
            </span>
          </div>
        )}

        {/* 중립 전환 메시지 */}
        {transitionMsg && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80 pointer-events-none">
            <span className="text-xl font-bold text-foreground-800 animate-pop-in whitespace-nowrap">
              {transitionMsg}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}